import { useCallback, useEffect, useState } from "react";
import type { Route } from "./route";
/* ── The basket ────────────────────────────────────────────────────────────────────────────── */
export interface BasketLine {
  variantId: string;
  handle: string;
  title: string;
  variantTitle: string;
  sku: string | null;
  image: string | null;
  /** For display only. The server re-prices at checkout. */
  priceCents: number | null;
  quantity: number;
  /** Product feed item id, for the ad tags only (absent on baskets saved before it existed). */
  feedId?: string;
}

/**
 * ⚠️ Versioned key. A basket saved under an older shape must not be parsed as this one; bumping
 * the suffix orphans the old key rather than crashing the shop on the first visit after a deploy.
 */
export const BASKET_KEY = "zs-basket-v1";

export function readBasket(): BasketLine[] {
  try {
    const raw = localStorage.getItem(BASKET_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as BasketLine[]).filter((l) => l && typeof l.variantId === "string") : [];
  } catch {
    return [];
  }
}

export function useBasket() {
  const [lines, setLines] = useState<BasketLine[]>(readBasket);
  useEffect(() => {
    try {
      localStorage.setItem(BASKET_KEY, JSON.stringify(lines));
    } catch {
      // Private mode or a full store: the basket still works for this page load.
    }
  }, [lines]);

  const add = useCallback((line: Omit<BasketLine, "quantity">, quantity: number) => {
    setLines((prev) => {
      const i = prev.findIndex((l) => l.variantId === line.variantId);
      if (i === -1) return [...prev, { ...line, quantity }];
      const next = [...prev];
      next[i] = { ...next[i]!, quantity: Math.min(500, next[i]!.quantity + quantity) };
      return next;
    });
  }, []);
  const setQty = useCallback((variantId: string, quantity: number) => {
    setLines((prev) =>
      quantity <= 0 ? prev.filter((l) => l.variantId !== variantId) : prev.map((l) => (l.variantId === variantId ? { ...l, quantity } : l)),
    );
  }, []);
  const clear = useCallback(() => setLines([]), []);
  const count = lines.reduce((n, l) => n + l.quantity, 0);
  const subtotalCents = lines.reduce((n, l) => n + (l.priceCents ?? 0) * l.quantity, 0);
  return { lines, add, setQty, clear, count, subtotalCents };
}
export type Basket = ReturnType<typeof useBasket>;

/**
 * Where a basket line links back to: its product page, or — for a shop bundle, whose line id is
 * `relay:<id>` (worker/src/shop-bundle-ids.ts) and whose `handle` is the bundle's slug — its bundle page.
 */
export const lineRoute = (l: Pick<BasketLine, "variantId" | "handle">): Route =>
  l.variantId.startsWith("relay:") ? { name: "bundle", slug: l.handle } : { name: "product", handle: l.handle };
